import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * Generic in-app text prompt matching the app's dbModal* convention (same look as
 * ConfirmModal and the database modals). Replaces window.prompt(), which renders the
 * browser's own chrome and ignores the design system entirely.
 *
 * Props:
 *   open         — when falsy, renders nothing
 *   title        — heading text
 *   description  — optional explanatory line under the title
 *   label        — label above the field
 *   placeholder  — field placeholder
 *   defaultValue — pre-filled answer (re-seeded every time the dialog opens)
 *   multiline    — render a textarea instead of a single-line input
 *   required     — when true (default) the confirm button stays disabled until non-blank
 *   maxLength    — optional cap on the answer
 *   variant      — "info" | "warn" | "danger" (drives badge/button colour)
 *   icon         — short glyph shown in the badge
 *   confirmLabel — label for the confirm button
 *   busy         — disables the confirm button while the caller is working
 *   onSubmit(value) / onCancel — callbacks. onSubmit gets the trimmed answer.
 */
export default function PromptModal({
  open,
  title,
  description,
  label,
  placeholder,
  defaultValue = "",
  multiline = false,
  required = true,
  maxLength,
  variant = "info",
  icon = "✏️",
  confirmLabel,
  busy = false,
  onSubmit,
  onCancel,
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState(defaultValue ?? "");
  const fieldRef = useRef(null);

  // Re-seed on open: a reused instance must never show the previous answer.
  useEffect(() => {
    if (open) setValue(defaultValue ?? "");
  }, [open, defaultValue]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onCancel(); };
    document.addEventListener("keydown", onKey);
    const focus = window.setTimeout(() => fieldRef.current?.focus(), 0);
    return () => {
      document.removeEventListener("keydown", onKey);
      window.clearTimeout(focus);
    };
  }, [open, onCancel]);

  if (!open) return null;

  const v = ["info", "warn", "danger"].includes(variant) ? variant : "info";
  const answer = value.trim();
  const canSubmit = !busy && (!required || answer.length > 0);

  const submit = (e) => {
    e?.preventDefault?.();
    if (canSubmit) onSubmit(answer);
  };

  const field = multiline ? (
    <textarea
      ref={fieldRef}
      className="dbModalConfirmInput dbModalPromptInput dbModalPromptInput--multiline"
      value={value}
      placeholder={placeholder}
      maxLength={maxLength}
      rows={3}
      onChange={(e) => setValue(e.target.value)}
      // Enter is a newline in a textarea, so offer the usual modifier shortcut instead.
      onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit(e); }}
    />
  ) : (
    <input
      ref={fieldRef}
      type="text"
      className="dbModalConfirmInput dbModalPromptInput"
      value={value}
      placeholder={placeholder}
      maxLength={maxLength}
      onChange={(e) => setValue(e.target.value)}
    />
  );

  return (
    <div className="dbModalOverlay" onClick={onCancel}>
      <form
        className="dbModalCard dbModalConfirmCard"
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
      >
        <button
          type="button"
          className="drawer-close-btn drawer-close-btn-abs"
          onClick={onCancel}
          aria-label={t("common.close", { defaultValue: "Close" })}
          title={t("common.close", { defaultValue: "Close" })}
        >
          <svg width="8" height="8" viewBox="0 0 8 8" fill="none" aria-hidden="true">
            <line x1="1" y1="1" x2="7" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            <line x1="7" y1="1" x2="1" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </button>

        {icon != null && (
          <div className={`dbModalConfirmBadge dbModalConfirmBadge--${v}`}>{icon}</div>
        )}

        <div className="dbModalTitle">{title}</div>

        {description && <div className="dbModalPromptDesc">{description}</div>}

        <div className="dbModalConfirmInputGroup">
          {label && <label className="dbModalConfirmLabel">{label}</label>}
          {field}
        </div>

        <div className="dbModalConfirmActions">
          <button type="button" className="dbModalCancel" onClick={onCancel}>
            {t("common.cancel", { defaultValue: "Cancel" })}
          </button>
          <button
            type="submit"
            className={`dbModalConfirmBtn dbModalConfirmBtn--${v}`}
            disabled={!canSubmit}
          >
            {confirmLabel || t("common.ok", { defaultValue: "OK" })}
          </button>
        </div>
      </form>
    </div>
  );
}
